import { QueryParamFactory } from '../../factories'
import { QueryParam } from './'

interface QueryParamObject {
  [key: string]: QueryParam<any>
}

export class Uri {
  private static URI_REGEX = Object.freeze(
    /^(?:([^:\/]+):)?(?:\/\/)?([^:\/\s?#]+)?(?:\/|(\/[\w\-\.]+[^#?\s]*))?(?:\?([^#\s]+))?(?:#(.*))?$/
  )

  private _protocol: string
  private _hostname: string
  private _domain: string
  private _path: string
  private _queryStringObject: QueryParamObject = {}
  private _hash: string
  private _origin: string
  private _port: string

  constructor(uriString: string, private _queryParamFactory = new QueryParamFactory()) {
    this._parseUri(this._encodeSpaces(uriString || ''))
  }

  public getProtocol(): string {
    return this._protocol
  }

  public getHostname(): string {
    return this._hostname
  }

  public getDomain(): string {
    return this.getHostname().replace(/(.+\.)*((.+\.)+?.{2,6})/g, '$2')
  }

  public getPath(): string {
    return this._path
  }

  public getQueryString(): string {
    const queryParams = Object.keys(this._queryStringObject).map((key: string) => {
      const queryParam = this._queryStringObject[key]
      return queryParam.toString()
    })

    return queryParams.join('&')
  }

  public getQueryStringObject(): QueryParamObject {
    return this._queryStringObject
  }

  public getQueryStringParam(key) {
    return this._queryStringObject[key] || this._queryParamFactory.createNull()
  }

  public getHash(): string {
    return this._hash
  }

  public getHost(): string {
    //  not implemented
    // return `${this.getHostname()}${this._port.trim() === '' ? '' : `:${this.getPort()}`}`
    return this.getHostname()
  }

  public getOrigin(): string {
    return `${this.getProtocol()}://${this.getHost()}`
  }

  //  not implemented
  // public getPort(): string {
  //   return this._port
  // }

  public setProtocol(protocol: string) {
    this._protocol = protocol
  }

  public setHostname(hostname: string) {
    this._hostname = hostname
  }

  public setPath(path: string) {
    if (typeof path === 'string' && !path.startsWith('/')) {
      path = `/${path}`
    }
    this._path = path
  }

  public setHash(hash: string) {
    this._hash = hash
  }

  //  not implemented
  // public setPort(port: string) {
  //   this._port = port
  // }

  public addPathPart(value: string) {
    value = value || ''
    if (typeof value !== 'string') { value = String(value) }
    this._path += `/${value}`
    this._path = this._path.replace(/\/\/+/g, '/')
  }

  public mergeSuffix(value: string) {
    if (!value.startsWith('/')) { value = `/${value}` }
    const match = Uri.URI_REGEX.exec(value)
    if (match === null) {
      throw new Error(`Cannot identify '${value}' as a suffix.`)
    }
    const pathPart = match[3]
    this.addPathPart(pathPart)
    this._processQueryString(decodeURIComponent(match[4] || ''))
    this.setHash(match[5] || this._hash)
  }

  public addQueryStringPart(key: string, value: string) {
    const pair = `${key}=${value}`
    const queryParam = this._queryParamFactory.create(pair)
    this._appendQueryParam(queryParam)
  }

  public mergeQueryString(queryStringObject: QueryParamObject) {
    Object.assign(this._queryStringObject, queryStringObject)
  }

  public removeQueryStringPart(key: string) {
    delete this._queryStringObject[key]
  }

  public toString(): string {
    const path = this.getPath() || ''
    const queryStringPart = this.getQueryString() === '' ? '' : ('?' + this.getQueryString())
    const hash = this.getHash() === undefined ? '' : `#${this.getHash()}`
    const host = this.getHost() || ''
    let result = `${path}${queryStringPart}${hash}`

    if (host !== '') {
      result = `//${host}${result}`
      if (typeof this.getProtocol() === 'string' && this.getProtocol().trim() !== '') {
        result = `${this.getProtocol()}:${result}`
      }
    }
    return result
  }

  public equals(uri: Uri, ...attrsToMatch): boolean {
    attrsToMatch = attrsToMatch.length ?
      attrsToMatch :
      ['protocol', 'hostname', 'domain', 'path', 'queryStringObject', 'hash', 'origin', 'port']
    return attrsToMatch.every((attr: string) => {
      return this[`_${attr.toLowerCase()}`] === uri[`_${attr.toLowerCase()}`]
    })
  }

  private _parseUri(uriString: string) {
    const match = Uri.URI_REGEX.exec(uriString)
    if (match === null) {
      throw new Error(`Cannot parse URI '${uriString}'`)
    }

    this._protocol = match[1]
    this._hostname = match[2]
    this._path = match[3] || '/'
    this._processQueryString(decodeURIComponent(match[4] || ''))
    this._hash = match[5]

    this._port = ''
  }

  private _processQueryString(queryString: string = '') {
    queryString.split('&').forEach((pair: string) => {
      if (pair !== '') {
        const queryParam = this._queryParamFactory.create(pair)
        this._appendQueryParam(queryParam)
      }
    })
  }

  private _appendQueryParam(queryParam: QueryParam<any>) {
    const actionableQueryParam = this._queryStringObject[queryParam.key] || queryParam
    actionableQueryParam.add(queryParam.value)
    this._queryStringObject[queryParam.key] = actionableQueryParam
  }

  private _encodeSpaces(value: string): string {
    return value.replace(/ /g, '%20')
  }

}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/core/models/uri/uri.ts